import React from "react";
import { AuthenticationResultStatus, authService } from "./AuthorizeService";
import { QueryParameterNames, LogoutActions, ApplicationPaths } from "./ApiAuthorizationConstants";
import Loading from "../BIMDigitalTwin/Common/Components/Loading";

// The main responsibility of this component is to handle the user's logout process.
// This is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
export class Logout extends React.PureComponent<
    { action: string },
    { message: string | null | undefined; isReady: boolean }
> {
    private static navigateToReturnUrl(returnUrl: string): void {
        return window.location.replace(returnUrl);
    }

    private static getReturnUrl(state: string | undefined): string {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error(
                "Invalid return url. The return url needs to have the same origin as the current page."
            );
        }
        return state || fromQuery || `${window.location.origin}${ApplicationPaths.LoggedOut}`;
    }

    constructor(props: { action: string }) {
        super(props);

        this.state = {
            message: undefined,
            isReady: false,
        };
    }

    componentDidMount(): void {
        const { action } = this.props;
        switch (action) {
            case LogoutActions.Logout:
                if (window.history.state.state.local) {
                    this.logout(Logout.getReturnUrl(undefined));
                } else {
                    // This prevents regular links to <app>/authentication/logout from triggering a logout
                    this.setState({
                        isReady: true,
                        message: "The logout was not initiated from within the page.",
                    });
                }
                break;
            case LogoutActions.LogoutCallback:
                this.processLogoutCallback();
                break;
            case LogoutActions.LoggedOut:
                this.setState({ isReady: true, message: "You successfully logged out!" });
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }

        this.populateAuthenticationState();
    }

    private async populateAuthenticationState(): Promise<void> {
        this.setState({ isReady: true });
    }

    private async processLogoutCallback(): Promise<void> {
        const url = window.location.href;
        const result = await authService.completeSignOut(url);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                // There should not be any redirects as the only time completeAuthentication finishes
                // is when we are doing a redirect sign in flow.
                throw new Error("Should not redirect.");
            case AuthenticationResultStatus.Success:
                await Logout.navigateToReturnUrl(Logout.getReturnUrl(result.returnUrl));
                break;
            case AuthenticationResultStatus.Fail:
                this.setState({ message: result.message });
                break;
            default:
                throw new Error("Invalid authentication result status.");
        }
    }

    private async logout(returnUrl: string): Promise<void> {
        const isauthenticated = await authService.isAuthenticated();
        if (isauthenticated) {
            const result = await authService.signOut(returnUrl);
            switch (result.status) {
                case AuthenticationResultStatus.Redirect:
                    break;
                case AuthenticationResultStatus.Success:
                    await Logout.navigateToReturnUrl(returnUrl);
                    break;
                case AuthenticationResultStatus.Fail:
                    this.setState({ message: result.message });
                    break;
                default:
                    throw new Error("Invalid authentication result status.");
            }
        } else {
            this.setState({ message: "You successfully logged out!" });
        }
    }

    render(): JSX.Element {
        const { isReady, message } = this.state;
        if (!isReady) {
            return <div />;
        }
        if (message) {
            return <div>{message}</div>;
        }
        const { action } = this.props;
        switch (action) {
            case LogoutActions.Logout:
                return <Loading label="Logging out" useLogo />;
            case LogoutActions.LogoutCallback:
                return <Loading label="Processing logout" useLogo />;
            case LogoutActions.LoggedOut:
                // TODO improve logged out message
                return <div>{message}</div>;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }
}
